import React from 'react';
import Router from 'next/router';
import cookie from 'cookie';
import { connect } from 'react-redux';
import smoothscroll from 'smoothscroll-polyfill';

import { trackPage, trackPageNew, trackPageForPartner, setUserType } from 'src/utils/analytics';
import { setReferrerCookie } from 'src/utils/cookie';
import { handleQueryParams } from 'src/modules/shared/modal/actions';
import { fetchUnreadMessagesCount, fetchUnreadNotificationsCount, fetchCurrentUser, fetchPermissions } from 'src/modules/shared/context/actions';
import { unsetServerSession } from 'src/modules/shared/auth/actions';
import { showDFPModal } from 'src/modules/shared/modal/actions';
import { init as initWebsockets } from 'src/modules/websockets/initWebsockets';
import { setSession, setResponseCookie } from 'src/utils/session';
import i18n from 'config/i18n';
import locales from 'config/locales';
import { translate } from 'react-i18next';
import { isUserBot } from 'src/utils/seo';

const isServer = typeof window === 'undefined';

const DEFAULT_LANG = locales.find(l => l.isDefault).lang;
const SUPPORTED_LANGUAGES = locales.map(l => l.lang);

const getLocale = (acceptLanguage) => {
  const locales = acceptLanguage.split(',');
  return locales.map(l => l.split('-')[0])
    .find(l => SUPPORTED_LANGUAGES.includes(l))
    || DEFAULT_LANG;
};

const WithSession = (Child) => {
  return class WrappedComponent extends React.Component {
    static defaultProps = {
      queryParams: {},
    };

    static async getInitialProps(context) {
      const { req, res, store: { dispatch, getState }, query } = context;
      let lang = null;
      let isIE = '';
      let i18nProps = {};

      if (req) { // server
        isIE = req.headers['user-agent'].indexOf('Trident/') >= 0;
        const { huSessID: sessionId, huLang } = cookie.parse(req.headers.cookie || '');

        // from cookie
        lang = huLang;

        // from browser language
        if (!lang && req.headers['accept-language']) {
          lang = getLocale(req.headers['accept-language']);
        }

        if (!isUserBot) {
          await setSession(sessionId, res, dispatch);
          setResponseCookie(res, 'huLang', lang); //<-- OK to have 'secure' set to true here? maybe set httpOnly to true also?
        }

        if (!process.browser) {
          i18nProps = i18n.getInitialProps(req, ['common']);
        }
        // only fetch user info for logged in user
        if (sessionId && !sessionId.endsWith('-000000000000')) {
          await dispatch(fetchCurrentUser());
        }
      }
      const childInitialProps = await Child.getInitialProps(context);
      let state = getState();

      if (state.context.currentUser) {
        const communityId = state.context.currentCommunity && state.context.currentCommunity.communityId;
        await dispatch(fetchPermissions(communityId));
        state = getState();
      }

      // from current user
      if (state.context.currentUser && state.context.lang) {
        lang = state.context.lang;
      }
      // default language
      if (!SUPPORTED_LANGUAGES.includes(lang)) {
        lang = DEFAULT_LANG;
      }

      if (res) {
        dispatch(unsetServerSession());
      }

      return {
        ...childInitialProps,
        ...i18nProps,
        queryParams: query,
        isLoggedIn: !!state.context.currentUser,
        currentUser: state.context.currentUser,
        community: state.context.currentCommunity,
        lang,
        isIE,
        initialLanguage: lang,
      };
    }

    constructor(props) {
      super(props);
      this.currentUrl = null;
    }

    componentDidMount = async () => {
      const { dispatch, currentUser, queryParams, isLoggedIn } = this.props;
      if (!isServer) {
        setUserType(currentUser);
      }
      this.onRouteLoaded();
      if(!isServer) {
        setReferrerCookie(document.referrer);
        dispatch(handleQueryParams(queryParams));

        if (isLoggedIn) {
          dispatch(fetchUnreadMessagesCount());
          dispatch(fetchUnreadNotificationsCount());
          initWebsockets(dispatch);
        }

        Router.onRouteChangeComplete = () => {
          this.onRouteLoaded();
        };

        window.triggerDFPPopup = (content) => {
          dispatch(showDFPModal(content));
        };
      }

      smoothscroll.polyfill();
    }

    componentWillUpdate(nextProps) {
      if (nextProps.isLoggedIn !== this.props.isLoggedIn && nextProps.isLoggedIn) {
        setUserType(nextProps.currentUser);
        // track again after login
        trackPage();
        trackPageNew();
      }
    }

    onRouteLoaded = () => {
      if (this.currentUrl !== location.pathname) {
        trackPage();
        trackPageNew();
        trackPageForPartner(this.props.community);

        this.currentUrl = location.pathname;
      }
    };

    render() {
      return (
        <Child {...this.props} />
      );
    }
  };
};

const WithSessionWrapped = child => connect()(
  WithSession(
    translate(['common'], {i18n, wait: process.browser})(child),
  ),
);
export default WithSessionWrapped;



// WEBPACK FOOTER //
// ./src/components/hoc/withSession.js